'use client'
import { MulticityFlightCard } from '@/src/components/desktop/srp/multicityFlightCard'
import { FlightCard as FlightCardType } from '@/src/types/flight'
import { TranslationFunction } from '@/src/types/translations'
import { MulticityFlightData } from '@/src/utils/srp/flightResultsUtil'
import { Box, Card, Typography } from '@mui/material'
import React, { useCallback } from 'react'
import ReactList from 'react-list'

const containerBoxSx = { p: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff', borderRadius: 1.5 }
const headerBoxSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 1,
    borderColor: 'grey.200',
    pb: 1,
    mb: 1.5
}
const routeTypographySx = { fontWeight: 600, color: 'grey.700' }
const cardSx = { borderRadius: 1, border: '1px solid #e0e0e0', overflow: 'hidden', boxShadow: 'none', p: 0 }
const mainFlightBoxSx = { px: 3, pt: 1.5, pb: 1.5 }

interface MulticityFlightResultsProps {
    groupedEntries: [string, FlightCardType[]][]
    getMulticityData: (flight: FlightCardType) => MulticityFlightData
    route?: string
    onBookFlight?: (flight: FlightCardType) => void
    onViewDetails?: (flight: FlightCardType) => void
    tCommon: TranslationFunction<'common'>
    tFlightDetails: TranslationFunction<'srpFlightDetails'>
    isBookingInProgress?: boolean
    loadingFlightKey?: string | null
}

const MulticityFlightResults: React.FC<MulticityFlightResultsProps> = ({
    groupedEntries = [],
    getMulticityData,
    route,
    onBookFlight,
    onViewDetails,
    tCommon,
    tFlightDetails,
    isBookingInProgress,
    loadingFlightKey
}) => {
    const renderCard = useCallback(
        (index: number, key: React.Key) => {
            const [, cards] = groupedEntries[index]
            const flight = cards[0]
            return (
                <Box key={key} sx={{ pb: 2 }}>
                    <Card sx={cardSx}>
                        <Box sx={mainFlightBoxSx}>
                            <MulticityFlightCard
                                data={getMulticityData(flight)}
                                onBook={() => onBookFlight?.(flight)}
                                onViewDetails={() => onViewDetails?.(flight)}
                                tCommon={tCommon}
                                tFlightDetails={tFlightDetails}
                                isBookingLoading={loadingFlightKey === flight.priceBreakup.fare.fk}
                                isDisabled={isBookingInProgress && loadingFlightKey !== flight.priceBreakup.fare.fk}
                            />
                        </Box>
                    </Card>
                </Box>
            )
        },
        [
            groupedEntries,
            getMulticityData,
            onBookFlight,
            onViewDetails,
            tCommon,
            tFlightDetails,
            isBookingInProgress,
            loadingFlightKey
        ]
    )

    return (
        <Box sx={containerBoxSx}>
            <Box sx={headerBoxSx}>
                <Typography variant="subtitle2" sx={routeTypographySx}>
                    {route}
                </Typography>
            </Box>
            <ReactList length={groupedEntries.length} itemRenderer={renderCard} threshold={300} />
        </Box>
    )
}

export { MulticityFlightResults }
export type { MulticityFlightResultsProps }
